import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyNoticePage() {
  return (
    <div className="min-h-screen bg-[#F9F9F6] noise-bg">
      {/* Header */}
      <header className="bg-[#1A2E4A] text-white py-6 px-8">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <h1 className="text-2xl">Digital Law Digest</h1>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors duration-300"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>
      
      {/* Content */}
      <main className="py-16 px-8 relative z-10" data-testid="privacy-notice-page">
        <article className="prose-editorial">
          <h1 className="text-4xl mb-8 text-[#1A2E4A]">Digital Law Digest Privacy Notice</h1>
          
          <p className="text-[#0A0F1A] leading-relaxed mb-8">
            This privacy notice tells you what to expect us to do with your personal information.
          </p>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Contact details</h2>
            <p>
              <a href="mailto:admin@digitallawdigest.com">admin@digitallawdigest.com</a>
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">What information we collect, use, and why</h2>
            <p>We collect or use the following information to provide services and goods, including delivery:</p>
            <ul>
              <li>Email address</li>
              <li>IP address</li>
            </ul>
            <p>We collect or use the following personal information for dealing with queries, complaints or claims:</p>
            <ul>
              <li>Your correspondence with us</li>
              <li>Email address</li>
            </ul>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Lawful basis</h2>
            <p>Our lawful basis for collecting or using personal information is:</p>
            <p>
              <strong>Legitimate interests</strong> — we're collecting or using your information because it benefits you, our organisation or someone else, without causing an undue risk of harm to anyone. All of your data protection rights may apply, except the right to portability. Our legitimate interests are to use your email to send the Digital Law Digest newsletter and to respond to any emails you send to us.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Where we get personal information from</h2>
            <ul>
              <li>Directly from you</li>
            </ul>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">How long we keep information</h2>
            <p>
              We only keep your personal information as long as is necessary for the purposes for which the personal information is processed. The criteria we use to determine the retention period are: (i) the respective statutory retention period; (ii) our contractual relationship with you; and (iii) to prepare for or deal with disputes, including potential disputes.
            </p>
            <p>
              If you unsubscribe from the newsletter, we will delete your data immediately, as long as it is no longer necessary to protect or defend our position or that of a third party.
            </p>
            <p>
              For more information on how long we store your personal information please contact us using the details provided above.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Who we share information with</h2>
            <h3 className="text-xl mb-3 text-[#1A2E4A]">Data processors</h3>
            <p>
              <strong>Twilio SendGrid:</strong> This data processor sends emails to you on our behalf.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Sharing information outside the UK</h2>
            <p>
              Where necessary, we will transfer personal information outside of the UK and EEA. When doing so, we comply with UK and EU GDPR, making sure appropriate safeguards are in place.
            </p>
            <table className="w-full border-collapse mt-4 mb-4">
              <tbody>
                <tr className="border-b border-[#E2E4E0]">
                  <td className="py-2 pr-4 font-medium">Organisation name:</td>
                  <td className="py-2">Twilio SendGrid</td>
                </tr>
                <tr className="border-b border-[#E2E4E0]">
                  <td className="py-2 pr-4 font-medium">Category of recipient:</td>
                  <td className="py-2">Communication platform</td>
                </tr>
                <tr className="border-b border-[#E2E4E0]">
                  <td className="py-2 pr-4 font-medium">Country the personal information is sent to:</td>
                  <td className="py-2">US</td>
                </tr>
                <tr className="border-b border-[#E2E4E0]">
                  <td className="py-2 pr-4 font-medium">How the transfer complies with UK and EU data protection law:</td>
                  <td className="py-2">The organisation is part of the EU-US Data Privacy Framework and the UK Extension to the EU-US DPF.</td>
                </tr>
              </tbody>
            </table>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">Data protection rights</h2>
            <ul>
              <li><strong>Your right of access</strong> — You have the right to ask us for copies of your personal information. You can request other information such as details about where we get personal information from and who we share personal information with. There are some exemptions which means you may not receive all the information you ask for.</li>
              <li><strong>Your right to rectification</strong> — You have the right to ask us to correct or delete personal information you think is inaccurate or incomplete.</li>
              <li><strong>Your right to erasure</strong> — You have the right to ask us to delete your personal information.</li>
              <li><strong>Your right to restriction of processing</strong> — You have the right to ask us to limit how we can use your personal information.</li>
              <li><strong>Your right to object to processing</strong> — You have the right to object to the processing of your personal data.</li>
              <li><strong>Your right to data portability</strong> — You have the right to ask that we transfer the personal information you gave us to another organisation, or to you.</li>
              <li><strong>Your right to withdraw consent</strong> — When we use consent as our lawful basis you have the right to withdraw your consent at any time.</li>
            </ul>
            <p className="mt-4">
              If you make a request, we must respond to you without undue delay and in any event within one month.
            </p>
            <p>
              To make a data protection rights request, please contact us using the contact details at the top of this privacy notice.
            </p>
          </section>
          
          <section className="mb-12">
            <h2 className="text-2xl mb-4 text-[#1A2E4A]">How to complain</h2>
            <p>
              If you have any concerns about our use of your personal data, you can make a complaint to us using the contact details at the top of this privacy notice.
            </p>
            <p>
              If you remain unhappy with how we've used your data after raising a complaint with us, you can also complain to the ICO or the national data protection authority of the country you are located in.
            </p>
            <p className="mt-4">
              <strong>Information Commissioner's Office</strong><br />
              Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />
              Helpline: 0303 123 1113<br />
              Website: <a href="https://www.ico.org.uk/make-a-complaint" target="_blank" rel="noopener noreferrer">https://www.ico.org.uk/make-a-complaint</a>
            </p>
            <h3 className="text-xl mt-6 mb-3 text-[#1A2E4A]">If you are located in the EU or EEA</h3>
            <p>
              You can complain to the national data protection authority of the EU or EEA country in which you are located. A full list of EU and EEA national data protection authorities and their contact details is available on the European Data Protection Board website at: <a href="https://www.edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" rel="noopener noreferrer">https://www.edpb.europa.eu/about-edpb/about-edpb/members_en</a>
            </p>
          </section>
          
          <p className="text-sm text-[#525C6A] mt-12">
            Last updated April 2026
          </p>
        </article>
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-[#E2E4E0] py-8 px-8">
        <div className="max-w-3xl mx-auto text-center text-sm text-[#525C6A]">
          <p>&copy; {new Date().getFullYear()} Digital Law Digest. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
